import { Text } from '@react-three/drei';

type Vec3 = [number, number, number];

const computeFontSize = (
	text: string,
	base: number,
	maxWidth: number,
	maxHeight: number,
	lineHeight = 1.2
): number => {
	if (!text) return base;
	const charsPerLine = Math.floor(maxWidth / (base * 0.55));
	const lines = Math.ceil(text.length / charsPerLine);
	const requiredHeight = lines * base * lineHeight;
	if (requiredHeight <= maxHeight) return base;
	return Math.max(base * (maxHeight / requiredHeight), base * 0.4);
};

interface LabelProps {
	title: string;
	manufacturer: string;
	subtitle: string;
	position?: Vec3;
	rotation?: Vec3;
	width?: number;
	height?: number;
	titleMaxChars?: number;
}

export const Label = ({
	title,
	manufacturer,
	subtitle,
	position = [0, 0, 0],
	rotation = [0, 0, 0],
	width = 6.5,
	height = 5,
	titleMaxChars = 30,
}: LabelProps) => {
	const z = 0.1;
	const topH = height * 0.22;
	const midH = height * 0.56;
	const botH = height * 0.22;
	const topY = midH / 2 + topH / 2;
	const botY = -midH / 2 - botH / 2;
	const maxWidth = width * 0.88;

	const displayTitle =
		title.length > titleMaxChars ? title.slice(0, titleMaxChars - 1) + '…' : title;
	const titleSize = computeFontSize(displayTitle, 0.6, maxWidth, midH * 0.85);
	const manufacturerSize = computeFontSize(manufacturer, 0.5, maxWidth, topH * 0.7);
	const subtitleSize = computeFontSize(subtitle, 0.5, maxWidth, botH * 0.7);

	return (
		<group position={position} rotation={rotation}>
			<mesh position={[0, 0, -0.01]} renderOrder={1}>
				<planeGeometry args={[width + 0.1, height + 0.1]} />
				<meshBasicMaterial color="#2a5a2a" depthTest={false} transparent />
			</mesh>

			<mesh position={[0, 0, 0]} renderOrder={2}>
				<planeGeometry args={[width, height]} />
				<meshBasicMaterial color="#f2f5f0" depthTest={false} transparent />
			</mesh>

			<mesh position={[0, topY, z]} renderOrder={3}>
				<planeGeometry args={[width, topH]} />
				<meshBasicMaterial color="#3a7a3a" depthTest={false} transparent />
			</mesh>

			<mesh position={[0, botY, z]} renderOrder={3}>
				<planeGeometry args={[width, botH]} />
				<meshBasicMaterial color="#1a3a6a" depthTest={false} transparent />
			</mesh>

			<Text
				position={[0, topY, z * 2]}
				fontSize={manufacturerSize}
				color="#ffffff"
				anchorX="center"
				anchorY="middle"
				maxWidth={maxWidth}
				textAlign="center"
				fontWeight="bold"
				renderOrder={4}
			>
				<meshBasicMaterial depthTest={false} transparent />
				{manufacturer || ''}
			</Text>

			<Text
				position={[0, 0, z * 2]}
				fontSize={titleSize}
				color="#1a4a1a"
				anchorX="center"
				anchorY="middle"
				maxWidth={maxWidth}
				textAlign="center"
				fontWeight="bold"
				renderOrder={4}
			>
				<meshBasicMaterial depthTest={false} transparent />
				{displayTitle || ''}
			</Text>

			<Text
				position={[0, botY, z * 2]}
				fontSize={subtitleSize}
				color="#ffffff"
				anchorX="center"
				anchorY="middle"
				fontWeight="bold"
				maxWidth={maxWidth}
				textAlign="center"
				renderOrder={4}
			>
				<meshBasicMaterial depthTest={false} transparent />
				{subtitle || ''}
			</Text>
		</group>
	);
};
